<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppTopbar from './components/AppTopbar.vue';
import MobileNav from './components/MobileNav.vue';
import AppFooter from './components/AppFooter.vue';
import ToastHost from './components/ToastHost.vue';
import BackToTop from './components/BackToTop.vue';
import PageBackdrop from './components/PageBackdrop.vue';
import RouteProgress from './components/RouteProgress.vue';
const CommandPalette = defineAsyncComponent(() => import('./components/CommandPalette.vue'));
const MiniCart = defineAsyncComponent(() => import('./components/cart/MiniCart.vue'));
const QuickViewDialog = defineAsyncComponent(() => import('./components/catalog/QuickViewDialog.vue'));
const CookieBanner = defineAsyncComponent(() => import('./components/CookieBanner.vue'));
import { useAuthStore } from './stores/auth';
import { useFavoritesStore } from './stores/favorites';
import { useCartStore } from './stores/cart';
import { useUiStore } from './stores/ui';

const auth = useAuthStore();
const favorites = useFavoritesStore();
const cart = useCartStore();
const ui = useUiStore();
const route = useRoute();

const chromeless = computed(() => Boolean(route.meta.chromeless));

// Mobile navigation drawer (burger menu). Hidden via CSS on lg+.
const mobileNavOpen = ref(false);

// Close on navigation and lock body scroll while the drawer is open.
watch(() => route.fullPath, () => (mobileNavOpen.value = false));
watch(mobileNavOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

// ⌘K / Ctrl+K toggles the command palette from anywhere, text fields
// included (that is the point of the chord). `/` stays with the inline
// SearchBox (AppTopbar).
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') mobileNavOpen.value = false;
  if (chromeless.value) return; // the palette lives in the storefront shell only
  if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    if (ui.paletteOpen) ui.closePalette();
    else ui.openPalette();
  }
}

// The drawer is display:none from lg up, but the body scroll lock would
// survive a resize/rotation across that breakpoint — close it instead.
const desktopMq = window.matchMedia('(min-width: 1024px)');
function onDesktopChange(e: MediaQueryListEvent) {
  if (e.matches) mobileNavOpen.value = false;
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown);
  desktopMq.addEventListener('change', onDesktopChange);
  await auth.fetchUser();
  // Bind the cart to the resolved identity. Calling with null when nobody
  // is signed in is the load-bearing case: it prevents stale localStorage
  // from a previous owner ever surfacing in this session, and purges any
  // legacy single-key v1 cart on first run.
  cart.bindUser(auth.user?.id ?? null);
  if (auth.isAuthenticated) await favorites.fetch();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  desktopMq.removeEventListener('change', onDesktopChange);
  document.body.style.overflow = '';
});

// Watch the actual user identity, not isAuthenticated, so a switch from
// user A → user B (rare but possible) also triggers a rebind. The cart
// store deletes the previous user's bucket on every transition, so signing
// out genuinely wipes the cart from the device.
watch(
  () => auth.user?.id ?? null,
  (newId, oldId) => {
    if (newId === oldId) return;
    cart.bindUser(newId);
    if (newId) {
      void favorites.fetch();
    } else {
      favorites.reset();
    }
  },
);
</script>

<template>
  <!--
    DIRECTION CONTRACT (2026-10 storefront redesign) — full record in DESIGN.md.
    THESIS: a dark marketplace counter, not a sidebar app; price, platform and
      offers live on the card, navigation is a three-band top bar.
    OWN-WORLD: navy-black neutrals, wordmark blue on actions, violet for
      featured, hairline borders, one sans with bold tabular prices.
    FORM: brief-pinned reference (Dribbble 27137094) adapted to GAMZOR colour.
  -->
  <RouteProgress />
  <div v-if="chromeless" class="min-h-screen text-text">
    <PageBackdrop />
    <RouterView />
  </div>
  <div v-else class="flex min-h-screen flex-col text-text">
    <PageBackdrop />
    <AppTopbar :nav-open="mobileNavOpen" @toggle-nav="mobileNavOpen = !mobileNavOpen" />
    <MobileNav :open="mobileNavOpen" @close="mobileNavOpen = false" />
    <main class="flex-1 overflow-x-clip">
      <!-- Route change: 140 ms fade + 6px rise in, 100 ms fade out (DESIGN.md
           › Motion). Keyed by the matched route record, so param/query
           changes (product → product, catalog filters) keep the page
           mounted exactly as before; the wrapper gives Transition one root
           even for multi-root views. -->
      <RouterView v-slot="{ Component, route: viewRoute }">
        <Transition
          name="page"
          mode="out-in"
          enter-active-class="transition-[opacity,transform] duration-140 ease-out motion-reduce:transition-none"
          leave-active-class="transition-opacity duration-100 ease-in motion-reduce:transition-none"
          enter-from-class="translate-y-1.5 opacity-0"
          leave-to-class="opacity-0"
        >
          <div :key="viewRoute.matched[0]?.path ?? viewRoute.path">
            <component :is="Component" />
          </div>
        </Transition>
      </RouterView>
    </main>
    <AppFooter />
    <BackToTop />
    <CommandPalette />
    <MiniCart />
  </div>
  <!-- Overlays shared by both layouts (fixed-position, order = stacking). -->
  <QuickViewDialog />
  <CookieBanner />
  <ToastHost />
</template>
